import axios from 'axios';
import { auth } from '../firebase'; // Import Firebase auth

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Add a delay utility function
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Function to get the auth token header with retry logic
const getAuthHeader = async () => {
  // Try up to 3 times with increasing delay
  for (let attempt = 0; attempt < 3; attempt++) {
    const user = auth.currentUser;
    if (user) {
      try {
        const token = await user.getIdToken();
        return { Authorization: `Bearer ${token}` };
      } catch (error) {
        console.error("Error getting auth token:", error);
        return {};
      }
    } else {
      // Wait before trying again (200ms, then 400ms, then 600ms)
      await delay(200 * (attempt + 1));
      console.log(`Waiting for auth to initialize (attempt ${attempt + 1})...`);
    }
  }
  console.warn("No user logged in for API request after retries");
  return {}; // Return empty headers after all retries fail
};

// --- Timesheet Service ---

const timesheetService = {
  getTimesheets: async (employeeId: number, companyId: number, weekOffset: number = 0) => {
    console.log(`Fetching timesheets for employee ${employeeId}, company ${companyId}, offset ${weekOffset}`);
    const headers = await getAuthHeader(); // Get auth header
    try {
      const response = await axios.get(`${API_BASE_URL}/timesheets`, {
        params: { employee_id: employeeId, company_id: companyId, week_offset: weekOffset },
        headers: headers // Include headers in the request
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching timesheets:', error);
      throw error;
    }
  },

  // MODIFY OTHER FUNCTIONS SIMILARLY
  getEmployees: async (companyId: number) => {
    console.log(`Fetching employees for company ${companyId}`);
    const headers = await getAuthHeader();
    try {
      const response = await axios.get(`${API_BASE_URL}/employees`, {
        params: { company_id: companyId },
        headers: headers
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching employees:', error);
      throw error;
    }
  },

  getInitialEmployee: async (companyId: number) => {
    console.log(`Fetching initial employee for company ${companyId}`);
     const headers = await getAuthHeader();
    try {
      const response = await axios.get(`${API_BASE_URL}/employees/initial`, {
        params: { company_id: companyId },
        headers: headers
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching initial employee:', error);
      throw error;
    }
  },

  generateReports: async (companyId: number | null, weekOffset: number, generateAll: boolean, employeeId?: number, recipientEmail?: string) => {
    console.log(`Generating reports: company=${companyId}, offset=${weekOffset}, all=${generateAll}, employee=${employeeId}, email=${recipientEmail}`);
    const headers = await getAuthHeader();
    try {
      const response = await axios.post(
        `${API_BASE_URL}/generate-reports`,
        null, // no request body needed; using query params instead
        {
          headers,
          params: {
            company_id: companyId,
            week_offset: weekOffset,
            generate_all: generateAll,
            employee_id: employeeId,
            recipient_email: recipientEmail
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error generating reports:', error);
      throw error;
    }
  },

  getWeeklySummary: async (companyId: number | null, weekOffset: number) => {
    console.log(`Fetching weekly summary: company=${companyId}, offset=${weekOffset}`);
    const headers = await getAuthHeader();
    try {
      const response = await axios.get(`${API_BASE_URL}/weekly-summary`, {
        params: { company_id: companyId, week_offset: weekOffset },
        headers: headers
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching weekly summary:', error);
      throw error;
    }
  },

  getMissingTimesheets: async (companyId: number | null, weekOffset: number, minHoursPerDay: number) => {
    console.log(`Fetching missing timesheets: company=${companyId}, offset=${weekOffset}, minHours=${minHoursPerDay}`);
    const headers = await getAuthHeader();
    try {
      const response = await axios.get(`${API_BASE_URL}/missing-timesheets`, {
        params: { company_id: companyId, week_offset: weekOffset, min_hours_per_day: minHoursPerDay },
        headers: headers
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching missing timesheets:', error);
      throw error;
    }
  },

};

export { timesheetService }; 